import React, { useEffect, useState } from 'react';
import {
  FiCamera,
  FiChevronLeft,
  FiCopy,
  FiImage,
  FiInfo,
  FiMic,
  FiSmile,
} from 'react-icons/fi';

const OPENING =
  'Hey, you seem cool! I’m part of an invite-only social club called Tribe, and they just gave members a few invites to share. You seem like you’d be a great fit, so I wanted to offer you one 😊';

const SIGNUP_MESSAGE =
  'Yayy! Create an account at jointribe.io and use my code GOODCOMPANY when you sign up :)';

const FREE_RESPONSE =
  'Yeah! Membership is free :)';

const FOLLOW_UP =
  'Hey! Just wanted to make sure you saw this :)';

const MAYA_AVATAR =
  'https://i.pinimg.com/1200x/b8/8d/6f/b88d6f9bc27f967e569640c198e6ba8a.jpg';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Now that you’ve found a strong prospect, the next step is starting a conversation. To make this simple, Tribe has created an outreach script. The script is a branching conversation guide with pre-written messages based on how someone replies. We ask that you start every conversation with the same opening invitation. After that, all you have to do is follow the branch that matches their response. Most conversations will follow one of three paths: they are interested immediately, they want more information, or they do not respond. If they are interested, send the signup message with your referral code. If they want more information, use the script to answer their questions until they are ready to join. If they do not respond or stop responding, gently follow up once. If they still do not respond, move on.',
  },
];

const STAGE_TIMINGS = [
  6500,  // Browse down the script
  12500, // Return to Opening invitation
  14500, // Copy opening invitation
  16500, // Paste opening invitation
  18500, // Send opening, Maya typing
  20500, // Maya asks if it is free
  22000, // Navigate to Is it free?
  24500, // Copy free response
  25800, // Paste free response
  27300, // Send response, Maya typing
  28800, // Maya expresses interest
  30200, // Navigate to Interested
  31800, // Copy signup response
  33000, // Paste signup response
  34500, // Send signup response / Interested
  36500, // More information
  39000, // No response
];

const SCRIPT_RESPONSES = [
  {
    key: 'opening',
    label: 'Opening invitation',
    message: OPENING,
  },
  {
    key: 'interested',
    label: 'They are interested',
    message: SIGNUP_MESSAGE,
  },
  {
    key: 'what-is-tribe',
    label: 'What is Tribe?',
    message:
      'Tribe helps people in their 20s meet through small, hosted gatherings around LA.',
  },
  {
    key: 'free',
    label: 'Is it free?',
    message: FREE_RESPONSE,
  },
  {
    key: 'no-response',
    label: 'No response',
    message: FOLLOW_UP,
  },
];

export default function OutreachScript({ audioStatus,playbackId,
 }) 
 {
  const [visualStage, setVisualStage] = useState(0);
  const [activeScriptIndex, setActiveScriptIndex] =
    useState(0);

  useEffect(() => {
    if (audioStatus !== 'Playing') {
      return undefined;
    }

    setVisualStage(0);
    setActiveScriptIndex(0);

    const timers = STAGE_TIMINGS.map((time, index) =>
      window.setTimeout(
        () => setVisualStage(index + 1),
        time
      )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [audioStatus, playbackId]);

  useEffect(() => {
    let timers = [];

    if (visualStage === 1) {
      // Slowly browse the available responses.
      [1, 2, 3, 4].forEach((index, position) => {
        timers.push(
          window.setTimeout(
            () => setActiveScriptIndex(index),
            500 + position * 600
          )
        );
      });
    } else if (visualStage === 2) {
      // Return to the standard opening invitation.
      [3, 2, 1, 0].forEach((index, position) => {
        timers.push(
          window.setTimeout(
            () => setActiveScriptIndex(index),
            200 + position * 300
          )
        );
      });
    } else if (visualStage === 7) {
      // Opening invitation → Is it free?
      [1, 2, 3].forEach((index, position) => {
        timers.push(
          window.setTimeout(
            () => setActiveScriptIndex(index),
            300 + position * 480
          )
        );
      });
    } else if (visualStage === 12) {
      // Is it free? → They are interested
      [2, 1].forEach((index, position) => {
        timers.push(
          window.setTimeout(
            () => setActiveScriptIndex(index),
            300 + position * 520
          )
        );
      });
    } else if (visualStage === 0) {
      setActiveScriptIndex(0);
    } else if (visualStage === 3) {
      setActiveScriptIndex(0);
    } else if (
      visualStage >= 8 &&
      visualStage <= 11
    ) {
      setActiveScriptIndex(3);
    } else if (visualStage >= 13) {
      setActiveScriptIndex(1);
    }

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [visualStage]);

  const showScript = [
    0,
    1,
    2,
    3,
    7,
    8,
    12,
    13,
  ].includes(visualStage);

  const isNavigating = [
    1,
    2,
    7,
    12,
  ].includes(visualStage);

  const isCopying = [
    3,
    8,
    13,
  ].includes(visualStage);

  return (
    <>
      <style>{STYLES}</style>

      <section className="outreach-training-slide">
        {showScript ? (
          <ScriptView
            activeIndex={activeScriptIndex}
            isNavigating={isNavigating}
            isCopying={isCopying}
          />
        ) : (
          <InstagramView
            key={`instagram-${visualStage}`}
            stage={visualStage}
          />
        )}
      </section>
    </>
  );
}

function ScriptView({
  activeIndex,
  isNavigating,
  isCopying,
}) {
  const activeResponse =
    SCRIPT_RESPONSES[activeIndex];

  return (
    <div
      className={[
        'script-view',
        isNavigating ? 'is-navigating' : '',
        isCopying ? 'is-copying' : '',
      ].join(' ')}
      style={{ '--active-row': activeIndex }}
    >
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <strong>Referral Dashboard</strong>
        </div>

        <nav className="dashboard-nav">
          <div className="dashboard-tab is-active">
            Outreach Script
          </div>
          <div className="dashboard-tab">Referrals</div>
          <div className="dashboard-tab">Earnings</div>
        </nav>

        <div className="referral-code-field">
          <span>YOUR CODE</span>
          <strong>GOODCOMPANY</strong>
          <FiCopy />
        </div>
      </header>

      <div className="script-workspace">
        <div className="script-carousel">
          {SCRIPT_RESPONSES.map(
            (response, index) => (
              <div
                className={[
                  'script-row',
                  index === activeIndex
                    ? 'is-active'
                    : '',
                ].join(' ')}
                key={response.key}
              >
                <span>
                  {String(index + 1).padStart(2, '0')}
                </span>

                <strong>{response.label}</strong>
              </div>
            )
          )}
        </div>

        <div className="selected-response">
          <span>SELECTED RESPONSE</span>

          <strong>{activeResponse.label}</strong>

          <p>{activeResponse.message}</p>

          <button type="button">
            <FiCopy />
            Copy response
          </button>
        </div>

        <svg
          className="script-cursor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M4 2.8v15.5l4.2-3.8 3.2 6.2 3-1.5-3.1-6.1 5.7-.8L4 2.8Z" />
        </svg>
      </div>
    </div>
  );
}

function InstagramView({ stage }) {
  const state = getInstagramState(stage);

  return (
    <div
      className={[
        'instagram-stage',
        state.caption ? 'has-caption' : '',
      ].join(' ')}
    >
      {state.caption && (
        <div className="response-caption">
          <span>{state.caption.number}</span>
          <strong>{state.caption.title}</strong>
          <small>{state.caption.action}</small>
        </div>
      )}

      <div className="instagram-dm">
        <header className="instagram-header">
          <FiChevronLeft />

          <img
            className="instagram-header-avatar"
            src={MAYA_AVATAR}
            alt=""
          />

          <div className="instagram-user">
            <strong>maya.moves</strong>
            <span>Active now</span>
          </div>

          <FiInfo className="instagram-info" />
        </header>

        <div
          className={[
            'instagram-thread',
            state.messages.length > 0
              ? 'has-focus'
              : '',
          ].join(' ')}
        >
          <div className="instagram-profile">
            <img src={MAYA_AVATAR} alt="Maya Young" />
            <strong>Maya Young</strong>
            <span>maya.moves · Instagram</span>
            <button type="button">View profile</button>
          </div>

          <div className="instagram-messages">
            {state.messages.map(
              (message, index) => (
                <div
                  className={[
                    'instagram-message',
                    message.side,
                    index === state.focusIndex
                      ? 'is-focused'
                      : '',
                  ].join(' ')}
                  key={`${stage}-${index}`}
                >
                  {message.text}
                </div>
              )
            )}

            {state.typing && (
              <div className="instagram-typing">
                <i />
                <i />
                <i />
              </div>
            )}

            {state.silence && (
              <div className="silence-indicator">
                <i />
                <i />
                <i />
                <span>No response</span>
              </div>
            )}
          </div>
        </div>

        <footer
          className={[
            'instagram-composer',
            state.composerText
              ? 'has-pasted-message'
              : '',
          ].join(' ')}
        >
          <FiCamera />

          <span>
            {state.composerText || 'Message…'}
          </span>

          {state.composerText ? (
            <button type="button">Send</button>
          ) : (
            <div>
              <FiMic />
              <FiImage />
              <FiSmile />
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}

function getInstagramState(stage) {
  const openingMessage = {
    side: 'outgoing',
    text: OPENING,
  };

  const freeQuestion = {
    side: 'incoming',
    text: 'Is it free?',
  };

  const freeAnswer = {
    side: 'outgoing',
    text: FREE_RESPONSE,
  };

  const interestMessage = {
    side: 'incoming',
    text: 'Okay, I’m interested! How do I join?',
  };

  const signupAnswer = {
    side: 'outgoing',
    text: SIGNUP_MESSAGE,
  };

  if (stage === 4) {
    return {
      messages: [],
      focusIndex: -1,
      composerText: OPENING,
    };
  }

  if (stage === 5) {
    return {
      messages: [openingMessage],
      focusIndex: 0,
      typing: true,
    };
  }

  if (stage === 6) {
    return {
      messages: [
        openingMessage,
        freeQuestion,
      ],
      focusIndex: 1,
    };
  }

  if (stage === 9) {
    return {
      messages: [
        openingMessage,
        freeQuestion,
      ],
      focusIndex: 1,
      composerText: FREE_RESPONSE,
    };
  }

  if (stage === 10) {
    return {
      messages: [
        openingMessage,
        freeQuestion,
        freeAnswer,
      ],
      focusIndex: 2,
      typing: true,
    };
  }

  if (stage === 11) {
    return {
      messages: [
        openingMessage,
        freeQuestion,
        freeAnswer,
        interestMessage,
      ],
      focusIndex: 3,
    };
  }

  if (stage === 14) {
    return {
      messages: [
        openingMessage,
        freeQuestion,
        freeAnswer,
        interestMessage,
      ],
      focusIndex: 3,
      composerText: SIGNUP_MESSAGE,
    };
  }

  if (stage === 15) {
    return {
      caption: {
        number: '01',
        title: 'Interested',
        action:
          'Send the signup message with your code.',
      },
      messages: [
        interestMessage,
        signupAnswer,
      ],
      focusIndex: 1,
    };
  }

  if (stage === 16) {
    return {
      caption: {
        number: '02',
        title: 'More information',
        action:
          'Find and send the matching answer.',
      },
      messages: [
        freeQuestion,
        freeAnswer,
      ],
      focusIndex: 1,
    };
  }

  return {
    caption: {
      number: '03',
      title: 'No response',
      action:
        'Follow up once. If they still do not reply, move on.',
    },
    messages: [
      openingMessage,
      {
        side: 'outgoing',
        text: FOLLOW_UP,
      },
    ],
    focusIndex: 1,
    silence: true,
  };
}

OutreachScript.title = 'How to use your script';
OutreachScript.steps = STEPS;

const STYLES = `
  .outreach-training-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 480px;
    height: 100%;
    padding: clamp(28px, 5vh, 54px)
      clamp(18px, 4vw, 48px);
    box-sizing: border-box;
    overflow: hidden;
    background: #ffffff;
    color: #111111;
  }

  .script-view {
    position: relative;
    width: min(820px, 100%);
    overflow: visible;
    background: #ffffff;
    border: 1px solid #dddddd;
    border-radius: 0;
    box-shadow: 0 18px 55px rgba(0, 0, 0, .09);
    animation: outreach-screen-in 480ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .dashboard-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 24px;
    min-height: 64px;
    padding: 0 18px;
    border-bottom: 1px solid #dddddd;
  }

  .dashboard-brand {
    display: flex;
    align-items: center;
    min-height: 34px;
  }

  .dashboard-brand strong {
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
  }

  .dashboard-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 22px;
    height: 64px;
  }

  .dashboard-tab {
    display: flex;
    align-items: center;
    height: 100%;
    color: #999999;
    border-bottom: 2px solid transparent;
    box-sizing: border-box;
    font-size: 10px;
    font-weight: 700;
  }

  .dashboard-tab.is-active {
    color: #111111;
    border-color: #111111;
  }

  .referral-code-field {
    display: grid;
    grid-template-columns: auto auto auto;
    align-items: center;
    gap: 7px;
    padding: 8px 9px;
    border: 1px solid #cccccc;
    border-radius: 7px;
    white-space: nowrap;
  }

  .referral-code-field span {
    color: #888888;
    font-size: 7px;
    font-weight: 800;
    letter-spacing: .08em;
  }

  .referral-code-field strong {
    font-size: 9px;
    font-weight: 850;
  }

  .referral-code-field svg {
    width: 12px;
    height: 12px;
  }

  .script-workspace {
    position: relative;
    display: grid;
    grid-template-columns:
      minmax(250px, .9fr)
      minmax(300px, 1.1fr);
    align-items: center;
    gap: clamp(38px, 6vw, 68px);
    min-height: 330px;
    padding: 27px 28px;
    box-sizing: border-box;
  }

  .script-carousel {
    border-top: 1px solid #dddddd;
  }

  .script-row {
    display: grid;
    grid-template-columns: 34px 1fr;
    align-items: center;
    min-height: 52px;
    box-sizing: border-box;
    color: #999999;
    border-bottom: 1px solid #dddddd;
    opacity: .3;
    transition:
      color 320ms ease,
      opacity 320ms ease,
      transform 420ms cubic-bezier(.22, 1, .36, 1);
  }

  .script-row span {
    font-size: 9px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .script-row strong {
    font-size: 13px;
    font-weight: 700;
  }

  .script-row.is-active {
    color: #111111;
    opacity: 1;
    transform: translateX(8px);
  }

  .selected-response {
    padding-left: 22px;
    border-left: 2px solid #111111;
    transition:
      opacity 300ms ease,
      filter 300ms ease;
  }

  .script-view.is-navigating
    .selected-response {
    opacity: .18;
    filter: blur(2px);
  }

  .selected-response > span {
    color: #777777;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: .11em;
  }

  .selected-response > strong {
    display: block;
    margin-top: 7px;
    font-size: 14px;
    font-weight: 800;
  }

  .selected-response p {
    max-width: 390px;
    margin: 14px 0 18px;
    color: #333333;
    font-size: 12px;
    line-height: 1.5;
  }

  .selected-response button {
    display: flex;
    align-items: center;
    gap: 7px;
    appearance: none;
    min-width: 122px;
    min-height: 38px;
    padding: 0 18px;
    background: #111111;
    color: #ffffff;
    border: 0;
    border-radius: 999px;
    cursor: pointer;
    font: inherit;
    font-size: 11px;
    font-weight: 800;
  }

  .selected-response button svg {
    width: 13px;
    height: 13px;
  }

  .script-cursor {
    position: absolute;
    z-index: 5;
    left: 36%;
    top: calc(
      26px + (var(--active-row) * 52px)
    );
    width: 24px;
    height: 24px;
    fill: #111111;
    opacity: 0;
    pointer-events: none;
    transition:
      top 420ms cubic-bezier(.22, 1, .36, 1);
  }

  .script-view.is-navigating
    .script-cursor {
    animation: cursor-scroll 650ms ease-in-out
      infinite;
  }

  .script-view.is-copying
    .script-cursor {
    animation: cursor-copy 1.35s ease-in-out
      forwards;
  }

  .script-view.is-copying
    .selected-response button {
    animation: copy-button-press 1.35s
      ease-in-out;
  }

  .instagram-stage {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(28px, 5vw, 60px);
    width: min(780px, 100%);
    animation: outreach-screen-in 480ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .instagram-stage.has-caption {
    display: grid;
    grid-template-columns:
      minmax(160px, 220px)
      minmax(300px, 330px);
    justify-content: center;
  }

  .response-caption {
    padding-top: 15px;
    border-top: 1px solid #111111;
  }

  .response-caption span,
  .response-caption strong,
  .response-caption small {
    display: block;
  }

  .response-caption span {
    color: #888888;
    font-size: 10px;
    font-weight: 800;
  }

  .response-caption strong {
    margin-top: 10px;
    font-size: clamp(22px, 3vw, 36px);
    font-weight: 850;
    letter-spacing: -.055em;
  }

  .response-caption small {
    max-width: 230px;
    margin-top: 11px;
    color: #666666;
    font-size: 11px;
    line-height: 1.45;
  }

  .instagram-dm {
    position: relative;
    width: min(300px, 88vw);
    height: 440px;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #d8d8d8;
    border-radius: 0;
    box-shadow: 0 16px 46px rgba(0, 0, 0, .09);
    box-sizing: border-box;
  }

  .instagram-header {
    display: grid;
    grid-template-columns: 22px 34px 1fr 20px;
    align-items: center;
    gap: 7px;
    height: 54px;
    padding: 0 12px;
    box-sizing: border-box;
    border-bottom: 1px solid #e2e2e2;
  }

  .instagram-header > svg {
    width: 19px;
    height: 19px;
    stroke-width: 2;
  }

  .instagram-header-avatar {
    width: 34px;
    height: 34px;
    object-fit: cover;
    border-radius: 50%;
  }

  .instagram-user {
    display: grid;
    gap: 2px;
  }

  .instagram-user strong,
  .instagram-user span {
    display: block;
  }

  .instagram-user strong {
    font-size: 11px;
    font-weight: 800;
  }

  .instagram-user span {
    color: #777777;
    font-size: 8px;
  }

  .instagram-info {
    justify-self: end;
  }

  .instagram-thread {
    display: flex;
    flex-direction: column;
    height: 386px;
    padding: 10px 14px 55px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .instagram-profile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 4px 0 10px;
    transition:
      opacity 320ms ease,
      filter 320ms ease;
  }

  .instagram-profile strong {
    margin-top: 4px;
    font-size: 11px;
  }

  .instagram-profile span {
    color: #888888;
    font-size: 8px;
  }

  .instagram-profile img {
    width: 61px;
    height: 61px;
    object-fit: cover;
    border-radius: 50%;
  }

  .instagram-profile button {
    margin-top: 4px;
    padding: 7px 12px;
    background: #efefef;
    border: 0;
    border-radius: 7px;
    font: inherit;
    font-size: 8px;
    font-weight: 750;
  }

  .instagram-thread.has-focus
    .instagram-profile {
    opacity: .32;
    filter: none;
  }

  .instagram-messages {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: flex-end;
    min-height: 0;
  }

  .instagram-message {
    width: fit-content;
    max-width: 82%;
    margin: 4px 0;
    padding: 9px 11px;
    box-sizing: border-box;
    border-radius: 18px;
    font-size: 8.5px;
    line-height: 1.4;
    transition:
      opacity 320ms ease,
      filter 320ms ease,
      transform 320ms ease;
  }

  .instagram-message.incoming {
    background: #eeeeee;
  }

  .instagram-message.outgoing {
    margin-left: auto;
    background: #3797f0;
    color: #ffffff;
  }

  .instagram-message:not(.is-focused) {
    opacity: .42;
    filter: none;
    transform: scale(.985);
  }

  .instagram-message.is-focused {
    animation: focused-message-in 420ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .instagram-typing {
    display: flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
    margin: 4px 0;
    padding: 10px 12px;
    background: #eeeeee;
    border-radius: 18px;
    animation: focused-message-in 420ms
      cubic-bezier(.22, 1, .36, 1);
  }

  .instagram-typing i {
    width: 5px;
    height: 5px;
    background: #777777;
    border-radius: 50%;
    animation: instagram-typing-dot 1.1s
      ease-in-out infinite;
  }

  .instagram-typing i:nth-child(2) {
    animation-delay: 140ms;
  }

  .instagram-typing i:nth-child(3) {
    animation-delay: 280ms;
  }

  .silence-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 9px;
    color: #888888;
  }

  .silence-indicator i {
    width: 4px;
    height: 4px;
    background: #888888;
    border-radius: 50%;
  }

  .silence-indicator span {
    margin-left: 5px;
    font-size: 8px;
  }

  .instagram-composer {
    position: absolute;
    right: 12px;
    bottom: 12px;
    left: 12px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    min-height: 38px;
    margin: 0;
    padding: 8px 10px;
    box-sizing: border-box;
    border: 1px solid #dddddd;
    border-radius: 999px;
    color: #888888;
  }

  .instagram-composer > svg {
    width: 17px;
    height: 17px;
  }

  .instagram-composer > span {
    overflow: hidden;
    font-size: 9px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .instagram-composer > div {
    display: flex;
    gap: 9px;
  }

  .instagram-composer > div svg {
    width: 15px;
    height: 15px;
  }

  .instagram-composer.has-pasted-message
    > span {
    color: #111111;
  }

  .instagram-composer button {
    appearance: none;
    padding: 0;
    background: transparent;
    color: #1677f2;
    border: 0;
    cursor: pointer;
    font: inherit;
    font-size: 10px;
    font-weight: 800;
  }

  @keyframes outreach-screen-in {
    from {
      opacity: 0;
      transform: translateY(10px) scale(.99);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes cursor-scroll {
    0%, 100% {
      opacity: 1;
      transform: translateY(-5px);
    }
    50% {
      opacity: 1;
      transform: translateY(5px);
    }
  }

  @keyframes cursor-copy {
    0% {
      left: 36%;
      top: calc(
        26px + (var(--active-row) * 52px)
      );
      opacity: 1;
      transform: scale(1);
    }
    68% {
      left: 73%;
      top: 77%;
      opacity: 1;
      transform: scale(1);
    }
    78% {
      left: 73%;
      top: 77%;
      opacity: 1;
      transform: scale(.84);
    }
    100% {
      left: 73%;
      top: 77%;
      opacity: 0;
      transform: scale(1);
    }
  }

  @keyframes copy-button-press {
    0%, 68%, 100% {
      transform: scale(1);
    }
    78% {
      transform: scale(.95);
    }
  }

  @keyframes focused-message-in {
    from {
      opacity: 0;
      transform: translateY(9px) scale(.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes instagram-typing-dot {
    0%, 60%, 100% {
      opacity: .35;
      transform: translateY(0);
    }
    30% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }

  @media (max-width: 700px) {
    .outreach-training-slide {
      min-height: 610px;
      padding: 24px 12px;
    }

    .script-workspace {
      grid-template-columns: 1fr;
      gap: 22px;
      min-height: 450px;
      padding: 20px 16px;
    }

    .dashboard-header {
      grid-template-columns: 1fr auto;
      gap: 10px;
      padding: 9px 12px 0;
    }

    .dashboard-nav {
      grid-column: 1 / -1;
      grid-row: 2;
      height: 42px;
    }

    .referral-code-field {
      grid-column: 2;
      grid-row: 1;
    }

    .selected-response {
      padding: 14px 0 0;
      border-top: 2px solid #111111;
      border-left: 0;
    }

    .script-row {
      min-height: 42px;
    }

    .script-cursor {
      left: auto;
      right: 3%;
      top: calc(
        22px + (var(--active-row) * 42px)
      );
    }

    .script-view.is-copying
      .script-cursor {
      animation: none;
    }

    .instagram-stage.has-caption {
      grid-template-columns: 1fr;
      gap: 18px;
    }

    .response-caption {
      width: min(300px, 88vw);
    }

    .response-caption strong {
      font-size: 23px;
    }

    .instagram-dm {
      width: min(300px, 88vw);
    }

    .instagram-message {
      max-width: 84%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .script-view,
    .instagram-stage,
    .script-row,
    .selected-response,
    .script-cursor,
    .selected-response button,
    .instagram-message,
    .instagram-typing {
      animation-duration: 1ms !important;
      transition-duration: 1ms !important;
    }
  }
`;
